// src/components/heatmaps/ImagesUploaded.jsx

import { useState, useRef, useEffect } from "react";
import styles from "../../css/heatmaps/ImagesUploaded.module.css";
import HeatmapPreviewModal from "./HeatmapPreviewModal";
import trashIcon from "../../img/icons/trash.svg";
import ButtonBtn from "../buttons/ButtonBtn";

const ImagesUploaded = ({
  images = [],
  handleUploadToServer,
  handleRemoveImage,
  handleRemoveAll,
  canSubmit = true,
  submitDisabledReason = "",
}) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [showFade, setShowFade] = useState(false);
  const [imageUrls, setImageUrls] = useState(new Map());
  const listRef = useRef(null);

  const handleCloseModal = () => setSelectedImage(null);

  // Správa object URLs pro prevenci memory leaks
  useEffect(() => {
    const newUrls = new Map();
    images.forEach((file, index) => {
      const key = `${file.name}-${file.size}-${index}`;
      if (!imageUrls.has(key)) {
        newUrls.set(key, URL.createObjectURL(file));
      } else {
        newUrls.set(key, imageUrls.get(key));
      }
    });

    // Revoke URLs that are no longer needed
    imageUrls.forEach((url, key) => {
      if (!newUrls.has(key)) {
        URL.revokeObjectURL(url);
      }
    });

    setImageUrls(newUrls);

    // Cleanup on unmount
    return () => {
      newUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [images]);

  // Helper function to get image URL
  const getImageUrl = (file, index) => {
    const key = `${file.name}-${file.size}-${index}`;
    return imageUrls.get(key);
  };

  // Detekce, jestli je ještě co scrollovat
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const updateFade = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      setShowFade(scrollTop + clientHeight < scrollHeight);
    };

    updateFade();
    el.addEventListener("scroll", updateFade);
    window.addEventListener("resize", updateFade);
    return () => {
      el.removeEventListener("scroll", updateFade);
      window.removeEventListener("resize", updateFade);
    };
  }, [images]);

  const formatSizeKb = (bytes) => {
    if (!Number.isFinite(bytes)) return "";
    const kb = Math.round(bytes / 1024);
    return `${kb} Kb`;
  };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.header}>Nahrávané soubory</div>

        {/* 1) Scrollovatelný seznam obrázků */}
        <div
          ref={listRef}
          className={`${styles.fileList} ${!showFade ? styles.hideFade : ""}`}
        >
          {images.map((file, i) => {
            const imageUrl = getImageUrl(file, i);
            if (!imageUrl) return null;

            return (
              <div key={i} className={styles.fileRow}>
                <div className={styles.left}>
                  <img
                    src={imageUrl}
                    className={styles.thumb}
                    alt={file.name}
                    onClick={() => setSelectedImage(file)}
                  />
                  <p className={styles.fileName} title={file.name}>
                    {file.name}
                  </p>
                </div>
                <div className={styles.right}>
                  <span className={styles.size}>{formatSizeKb(file.size)}</span>
                  <button
                    onClick={() => handleRemoveImage(file)}
                    className={styles.trashBtn}
                    aria-label={`Smazat ${file.name}`}
                    title="Smazat"
                  >
                    <img src={trashIcon} alt="" className={styles.trashIcon} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* 2) Spodní akce */}
        {images.length > 0 && (
          <div className={styles.footerBar}>
            <button
              className={`${styles.removeAllLink} link`}
              onClick={handleRemoveAll}
            >
              smazat vše
            </button>
            <ButtonBtn
              onClick={handleUploadToServer}
              disabled={!canSubmit}
              title={
                !canSubmit && submitDisabledReason
                  ? submitDisabledReason
                  : undefined
              }
              marginBlock="0"
              justifyContent="flex-end"
              type="button"
            >
              Otestovat vše ({images.length})
            </ButtonBtn>
          </div>
        )}
      </div>

      {/* 3) Modal pro náhled */}
      {selectedImage && (
        <HeatmapPreviewModal
          onClose={handleCloseModal}
          imageSrc={getImageUrl(selectedImage, images.indexOf(selectedImage))}
          title={selectedImage.name}
          bgOpacity={1}
        />
      )}
    </>
  );
};

export default ImagesUploaded;
